import React, { useState, useEffect } from "react";

// ── Star rating ──────────────────────────────────────────────
const StarRating = ({ value, onChange, hasError }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className={`star-rating ${hasError ? "has-error" : ""}`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`star-btn ${star <= (hovered || value) ? "active" : ""}`}
          onClick={() => onChange(star === value ? 0 : star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          aria-label={`Rate ${star} out of 5`}
        >
          &#9733;
        </button>
      ))}
      {value > 0 && (
        <span className="star-value-label">{value} / 5</span>
      )}
    </div>
  );
};

// ── Slider input ─────────────────────────────────────────────
const SliderInput = ({ value, onChange, minLabel, maxLabel }) => {
  const displayVal = value ?? 5;
  return (
    <div className="slider-wrap">
      <div className="slider-labels">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
      <input
        type="range"
        min={1}
        max={10}
        value={displayVal}
        onChange={(e) => onChange(Number(e.target.value))}
        className="survey-range"
        style={{ "--pct": `${((displayVal - 1) / 9) * 100}%` }}
      />
      <div className="slider-tick-row">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
          <span key={n} className={`slider-tick ${n === displayVal ? "active" : ""}`}>
            {n}
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Main survey form ─────────────────────────────────────────
// surveyStructure shape (from API or static fallback via surveyApi.js):
// {
//   steps:           [{ key, label, shortLabel, count }],
//   departmentKeys:  { "A": "hr", "B": "finance", ... },
//   departmentLabels:{ "hr": "HR / Talent Acquisition", ... },
//   sections: {
//     A: [...questions],
//     B: { hr: [...], finance: [...], ... },
//     C: [...questions],
//     D: [...questions]
//   }
// }
const Section2 = ({ contactInfo, onComplete, surveyStructure }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [answers, setAnswers] = useState({});
  const [otherText, setOtherText] = useState("");
  const [errorIds, setErrorIds] = useState([]);

  // Scroll to top of survey on step change
  useEffect(() => {
    const el = document.querySelector(".survey-form-main");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentStep]);

  // All hooks must be above this guard
  if (!surveyStructure) return null;

  const { steps, departmentKeys, departmentLabels, sections } = surveyStructure;

  const getDeptKey = () => {
    const deptQ = sections.A.find((q) => q.isDepartmentSelect);
    if (!deptQ) return null;
    const selected = answers[deptQ.id];
    return selected ? departmentKeys[selected] : null;
  };

  const getQuestions = () => {
    const step = steps[currentStep];
    if (step.key === "A") return sections.A;
    if (step.key === "B") {
      const dk = getDeptKey();
      return dk ? (sections.B[dk] || []) : [];
    }
    if (step.key === "C") return sections.C;
    if (step.key === "D") return sections.D;
    return [];
  };

  const setAnswer = (id, val) => {
    setAnswers((prev) => ({ ...prev, [id]: val }));
    setErrorIds((prev) => prev.filter((e) => e !== id));
  };

  const validate = () => {
    const unanswered = getQuestions()
      .filter((q) => {
        if (q.type === "slider") return false;
        if (q.type === "star") return !answers[q.id] || answers[q.id] === 0;
        return !answers[q.id];
      })
      .map((q) => q.id);
    setErrorIds(unanswered);
    return unanswered.length === 0;
  };

  const handleNext = () => {
    if (!validate()) {
      const el = document.querySelector(".survey-form-main");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const newCompleted = [...new Set([...completedSteps, currentStep])];
    setCompletedSteps(newCompleted);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete({ contactInfo, answers, otherIndustry: otherText });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleStepClick = (idx) => {
    if (completedSteps.includes(idx)) setCurrentStep(idx);
  };

  const step = steps[currentStep];
  const questions = getQuestions();
  const deptKey = getDeptKey();
  const stepBLabel = deptKey ? departmentLabels[deptKey] : "Your Department";
  const stepLabel = step.key === "B" ? stepBLabel : step.label;
  const progressPct = (completedSteps.length / steps.length) * 100;

  // ── Question renderer ───────────────────────────────────────
  const renderQuestion = (q, idx) => {
    // console.log(answers);
    const hasError = errorIds.includes(q.id);
    return (
      <div key={q.id} className={`survey-question ${hasError ? "sq-error" : ""}`}>
        <div className="sq-header">
          <span className="sq-code">{idx + 1}</span>
          <h3 className="sq-text">{q.text}</h3>
        </div>
        {q.hint && <p className="sq-hint">{q.hint}</p>}

        {q.type === "radio" && (
          <div className={`radio-opts ${q.options.length > 4 ? "opts-wide" : ""}`}>
            {q.options.map((opt, optIdx) => (
              <button
                key={opt.id}
                type="button"
                className={`radio-opt ${answers[q.id] === opt.id ? "selected" : ""}`}
                onClick={() => {
                  if (
                    q.isDepartmentSelect &&
                    answers[q.id] !== undefined &&
                    answers[q.id] !== opt.id
                  ) {
                    // Department changed — reset all Section B, C, D answers and step completions
                    const allBIds = Object.values(sections.B).flat().map((bq) => bq.id);
                    const idsToReset = [
                      ...allBIds,
                      ...sections.C.map((cq) => cq.id),
                      ...sections.D.map((dq) => dq.id),
                    ];
                    setAnswers((prev) => {
                      const next = { ...prev };
                      idsToReset.forEach((id) => delete next[id]);
                      next[q.id] = opt.id;
                      return next;
                    });
                    setCompletedSteps((prev) => prev.filter((s) => s === 0));
                    setErrorIds([]);
                    setOtherText("");
                  } else {
                    setAnswer(q.id, opt.id);
                    if (q.hasOther && !opt.isOther) setOtherText("");
                  }
                }}
              >
                <span className="opt-letter">{String.fromCharCode(65 + optIdx)}</span>
                <span className="opt-text">{opt.label}</span>
              </button>
            ))}
            {q.hasOther && q.options.find((o) => o.isOther)?.id === answers[q.id] && (
              <input
                type="text"
                className="other-input"
                placeholder="Please specify your industry..."
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
              />
            )}
          </div>
        )}

        {q.type === "star" && (
          <StarRating
            value={answers[q.id] || 0}
            onChange={(val) => setAnswer(q.id, val)}
            hasError={hasError}
          />
        )}

        {q.type === "slider" && (
          <SliderInput
            value={answers[q.id]}
            onChange={(val) => setAnswer(q.id, val)}
            minLabel={q.minLabel}
            maxLabel={q.maxLabel}
          />
        )}

        {hasError && (
          <p className="sq-error-msg">Please answer this question to continue.</p>
        )}
      </div>
    );
  };

  return (
    <div className="survey-form-main">
      {/* ── Progress bar ── */}
      <div className="survey-progress-bar-wrap">
        <div
          className="survey-progress-fill"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* ── Step tabs ── */}
      <div className="survey-step-tabs">
        {steps.map((s, i) => {
          const isCompleted = completedSteps.includes(i);
          const isCurrent = i === currentStep;
          const isLocked = !isCompleted && !isCurrent;
          const label = s.key === "B" ? stepBLabel : s.label;
          return (
            <button
              key={s.key}
              type="button"
              className={`step-tab ${isCurrent ? "tab-current" : ""} ${isCompleted ? "tab-done" : ""} ${isLocked ? "tab-locked" : ""}`}
              onClick={() => handleStepClick(i)}
              disabled={isLocked}
              title={label}
            >
              <span className="tab-letter">{s.key}</span>
              <span className="tab-label">{s.shortLabel}</span>
            </button>
          );
        })}
      </div>

      {/* ── Section heading ── */}
      <div className="container">
        <div className="survey-section-head">
          <div className="ssh-meta">
            <span className="ssh-step">Section {step.key}</span>
            <span className="ssh-count">{questions.length} questions</span>
          </div>
          <h2 className="ssh-title">
            <span className="yellow-txt">{step.key}.</span> {stepLabel}
          </h2>
          {errorIds.length > 0 && (
            <p className="section-error-banner">
              Please answer all highlighted questions before continuing.
            </p>
          )}
        </div>

        {/* ── Questions ── */}
        <div className="survey-questions">
          {questions.map((q, i) => renderQuestion(q, i))}
        </div>

        {/* ── Navigation ── */}
        <div className="survey-nav-row">
          {currentStep > 0 ? (
            <button type="button" className="snav-btn snav-back" onClick={handleBack}>
              &#8592; Previous
            </button>
          ) : (
            <span />
          )}
          <button type="button" className="snav-btn snav-next" onClick={handleNext}>
            {currentStep === steps.length - 1 ? "Submit Survey" : "Next Section →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Section2;
